import React, { useState } from 'react';
import { Autocomplete, TextField, Chip, Box, CircularProgress, Typography } from '@mui/material';
import { FiPlus } from 'react-icons/fi';
import { colors } from '../theme/modernTheme';
import TagDialog from './TagDialog';
import { useAuth } from '../context/AuthContext';

/**
 * Enhanced Autocomplete component for tag selection with "Add Tag" functionality
 *
 * @param {Array} options - Available tags to select from
 * @param {Array} value - Currently selected tags
 * @param {Function} onChange - Callback when selection changes (event, newValue)
 * @param {Function} onTagCreated - Optional callback when a new tag is created
 * @param {Boolean} loading - Whether tags are loading
 * @param {Boolean} multiple - Whether multiple selection is enabled (default: true)
 * @param {String} placeholder - Placeholder text
 * @param {Object} sx - Additional styles for the TextField
 * @param {Object} autocompleteProps - Additional props to pass to Autocomplete
 */
export default function TagAutocomplete({
  options = [],
  value = [],
  onChange,
  onTagCreated,
  loading = false,
  multiple = true,
  placeholder = 'Select tags',
  sx = {},
  autocompleteProps = {},
}) {
  const { hasModulePermission, MODULE_PERMISSIONS } = useAuth();
  const tagPermission = hasModulePermission(MODULE_PERMISSIONS.TAGS);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [availableTags, setAvailableTags] = useState(options);
  const [inputValue, setInputValue] = useState('');
  const [tagNameForDialog, setTagNameForDialog] = useState('');
  const lastSearchRef = React.useRef('');

  const handleOpenTagDialog = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setTagNameForDialog(lastSearchRef.current || inputValue);
    setTagDialogOpen(true);
    setInputValue('');
    lastSearchRef.current = '';
  };

  const addTagStyle = {
    display: 'flex !important',
    alignItems: 'center',
    gap: 1,
    px: 1,
    py: '6px',
    my: '3px',
    minHeight: '32px',
    backgroundColor: 'transparent !important',
    borderRadius: '0px !important',
    color: (colors.primary?.main || '#373DFA') + ' !important',
    fontWeight: '600 !important',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: `${colors.action?.hover || 'rgba(0,0,0,0.04)'} !important`,
    },
  };

  // Update available tags when options change
  React.useEffect(() => {
    setAvailableTags(options);
  }, [options]);

  const handleTagDialogClose = () => {
    setTagDialogOpen(false);
    setTagNameForDialog('');
  };

  const handleTagCreated = (createdTag) => {
    if (createdTag) {
      // Add the new tag to available options
      setAvailableTags((prev) => [...prev, createdTag]);

      // Add the new tag to the selection
      if (multiple) {
        const newValue = [...(value || []), createdTag];
        onChange && onChange(null, newValue);
      } else {
        onChange && onChange(null, createdTag);
      }

      // Call the optional callback
      onTagCreated && onTagCreated(createdTag);
    }

    // Clear the input value after tag creation
    setInputValue('');
  };

  const renderOption = (props, option) => {
    // Special "Add Tag" option
    if (option.isAddNew && tagPermission) {
      return (
        <Box component="li" {...props} sx={addTagStyle} onClick={handleOpenTagDialog}>
          <FiPlus size={16} />
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Add Tag</Typography>
        </Box>
      );
    }

    // Regular tag option
    return (
      <Box
        component="li"
        {...props}
        sx={{
          display: 'flex !important',
          alignItems: 'center',
          px: 1,
          py: '6px',
          my: '3px',
          minHeight: '32px',
          backgroundColor: 'transparent !important',
          borderRadius: '0px !important',
          '&:hover': {
            backgroundColor: `${colors.action.hover} !important`,
            borderRadius: '0px !important',
          },
        }}
      >
        <Chip
          label={option.name}
          size="small"
          sx={{
            backgroundColor: option.color || 'rgba(139, 95, 191, 0.1)',
            color: option.color ? '#fff' : '#8B5FBF',
            fontWeight: 500,
            fontSize: '0.75rem',
            borderRadius: '4px',
          }}
        />
      </Box>
    );
  };

  // Add "Add Tag" option to the top of the list
  const optionsWithAddNew = [
    ...(tagPermission ? [{ id: 'add-new', name: 'Add Tag', isAddNew: true }] : []),
    ...availableTags,
  ];

  return (
    <>
      <Autocomplete
        multiple={multiple}
        size="small"
        options={optionsWithAddNew}
        value={multiple ? value : value && value.length > 0 ? value[0] : null}
        onChange={(event, newValue) => {
          // Filter out the "Add Tag" option if it was somehow selected
          if (multiple) {
            const filteredValue = newValue.filter((v) => !v.isAddNew);
            onChange && onChange(event, filteredValue);
          } else {
            // For single selection, don't select if it's the "Add Tag" option
            if (newValue?.isAddNew) {
              return;
            }
            onChange && onChange(event, newValue);
          }
        }}
        getOptionLabel={(option) => option.name || ''}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        filterSelectedOptions
        loading={loading}
        disableCloseOnSelect={multiple}
        inputValue={inputValue}
        onInputChange={(event, newInputValue, reason) => {
          setInputValue(newInputValue);
          if (reason === 'input') {
            lastSearchRef.current = newInputValue;
          } else if (reason === 'clear') {
            lastSearchRef.current = '';
          }
        }}
        forcePopupIcon={true}
        noOptionsText={
          tagPermission && (
            <Box
              sx={{ ...addTagStyle, width: '100%', boxSizing: 'border-box' }}
              onClick={handleOpenTagDialog}
            >
              <FiPlus size={16} />
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Add Tag</Typography>
            </Box>
          )
        }
        slotProps={{
          paper: {
            sx: {
              borderRadius: '4px',
              maxHeight: '200px',
              '& .MuiAutocomplete-listbox': {
                padding: '0px',
                maxHeight: '200px',
              },
              '& .MuiAutocomplete-noOptions': {
                padding: '0px',
              },
              '& .MuiAutocomplete-option': {
                fontSize: '0.875rem',
                px: 1,
                py: '6px',
                my: '3px',
                minHeight: '32px',
                backgroundColor: 'transparent !important',
                borderRadius: '0px !important',
                '&:hover': {
                  backgroundColor: `${colors.action.hover} !important`,
                  borderRadius: '0px !important',
                },
                "&[aria-selected='true']": {
                  backgroundColor: `${colors.action.hover} !important`,
                  borderRadius: '0px !important',
                },
              },
            },
          },
        }}
        componentsProps={{
          popper: {
            placement: 'bottom-start',
            modifiers: [
              {
                name: 'flip',
                enabled: false,
              },
            ],
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={multiple && value?.length > 0 ? '' : placeholder}
            inputProps={{
              ...params.inputProps,
            }}
            InputProps={{
              ...params.InputProps,
              style: { fontSize: '0.875rem' },
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '4px' },
              ...sx,
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={option.name}
              size="small"
              sx={{
                borderRadius: '4px',
                backgroundColor: option.color || 'rgba(139, 95, 191, 0.1)',
                color: option.color ? '#fff' : '#8B5FBF',
                fontWeight: 500,
                fontSize: '0.75rem',
              }}
            />
          ))
        }
        renderOption={renderOption}
        {...autocompleteProps}
      />

      <TagDialog
        open={tagDialogOpen}
        onClose={handleTagDialogClose}
        tag={null}
        onSuccess={handleTagCreated}
        initialName={tagNameForDialog}
      />
    </>
  );
}
